import { NO_TEXT, type Translated } from '@myre/common-i18n';
import { appear } from '@myre/ui-primitive-core';
import {
  DropdownComponent,
  DropdownTemplate,
  type ISelectItem,
} from '@myre/ui-primitive-forms';

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  type OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { NavbarIconBtnComponent } from '../navbar-icon-btn/navbar-icon-btn.component';

@Component({
  selector: 'myre-nav-search',
  templateUrl: './navbar-search.component.html',
  styleUrls: ['./navbar-search.component.scss'],
  animations: [appear(300, 0)],
  host: {
    '[style.min-width]': 'shortWidth',
  },
  imports: [
    DropdownComponent,
    FormsModule,
    DropdownTemplate,
    NgTemplateOutlet,
    NavbarIconBtnComponent,
  ],
})
export class NavbarSearchComponent<T> implements OnInit {
  private _changeDetectionRef = inject(ChangeDetectorRef);

  public value: string = '';
  public showSearch: boolean;

  @ViewChild('dropDown', { static: false })
  public dropDown: DropdownComponent<T>;

  @Input()
  public placeholder: Translated = NO_TEXT;

  @Input()
  public longPlaceholder: Translated = NO_TEXT;

  @Input()
  public items: ISelectItem<T>[] = [];

  @Input()
  public template: TemplateRef<any>;

  @Input()
  public shortWidth: string = '200px';

  @Input()
  public longWidth: string = '500px';

  @Input()
  public stretch: boolean = true;

  @Input()
  public adaptWidth: boolean = true;

  @Input()
  public startWithButtonView: boolean;

  @Input()
  public tertiary: boolean = true;

  @Output()
  public onInput = new EventEmitter<string>();

  @Output()
  public onSelect = new EventEmitter<T>();

  @Output()
  public onFocus = new EventEmitter<void>();

  @Output()
  public onBlur = new EventEmitter<void>();

  private _focused: boolean = false;
  private _stretchToShortCount: number = 0;

  public ngOnInit(): void {
    this.showSearch = !this.startWithButtonView;
  }

  public emitOnFocus(): void {
    this.onFocus.emit();
  }

  public emitOnBlur(): void {
    this.onBlur.emit();
  }

  public emitOnInput(input: string): void {
    this.onInput.emit(input);
  }

  public emitOnSelect(item: T | T[]): void {
    this.onSelect.emit(item as T);
  }

  public activateSearch(): void {
    this.showSearch = true;
  }

  @HostListener('document:click.out-zone', ['$event.target'])
  public clickOutside(target: EventTarget): void {
    if (
      this.startWithButtonView &&
      this.showSearch &&
      this.dropDown &&
      this._focused &&
      target instanceof Node &&
      !this.dropDown.rootElement().nativeElement.contains(target)
    ) {
      this.dropDown.updateIsFocused(false);
      this._focused = false;
      this.emitOnBlur();
      this._changeDetectionRef.detectChanges();
    }
  }

  public onStretchDone(e: any): void {
    if (this.startWithButtonView) {
      if (e.toState === 'short') {
        this._stretchToShortCount += 1;
        this._focused = false;
      }

      if (
        !this.dropDown.focused &&
        !this._focused &&
        !(this._stretchToShortCount % 2)
      ) {
        this.showSearch = false;
      }
    }
  }

  public onInitDone(): void {
    if (this.startWithButtonView) {
      this.dropDown.clickInside();
      this._focused = true;
      this.emitOnFocus();
    }
  }
}
